import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { vetrineContentService } from '../../../services/vetrineContentService';

const ProductsManager = ({ storeId }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState({
    nome: '',
    descrizione: '',
    categoria: '',
    prezzo: '',
    prezzoScontato: '',
    immagine: '',
    disponibile: true,
    nuovo: false,
    inEvidenza: false,
    ordine: 0
  });

  useEffect(() => {
    loadProducts();
  }, [storeId]);

  const loadProducts = async () => {
    try {
      setLoading(true);
      const data = await vetrineContentService?.getProductsByStore(storeId);
      setProducts(data);
    } catch (error) {
      console.error('Error loading products:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    try {
      const productData = {
        ...formData,
        storeId,
        prezzo: parseFloat(formData?.prezzo) || 0,
        prezzoScontato: formData?.prezzoScontato ? parseFloat(formData?.prezzoScontato) : null
      };

      if (editingProduct) {
        await vetrineContentService?.updateProduct(editingProduct?.id, productData);
      } else {
        await vetrineContentService?.createProduct(productData);
      }

      await loadProducts();
      resetForm();
    } catch (error) {
      console.error('Error saving product:', error);
      alert('Errore nel salvare il prodotto: ' + error?.message);
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      nome: product?.nome || '',
      descrizione: product?.descrizione || '',
      categoria: product?.categoria || '',
      prezzo: product?.prezzo || '',
      prezzoScontato: product?.prezzoScontato || '',
      immagine: product?.immagine || '',
      disponibile: product?.disponibile ?? true,
      nuovo: product?.nuovo ?? false,
      inEvidenza: product?.inEvidenza ?? false,
      ordine: product?.ordine || 0
    });
    setShowForm(true);
  };

  const handleDelete = async (productId) => {
    if (!confirm('Sei sicuro di voler eliminare questo prodotto?')) return;
    
    try {
      await vetrineContentService?.deleteProduct(productId);
      await loadProducts();
    } catch (error) {
      console.error('Error deleting product:', error);
      alert('Errore nell\'eliminare il prodotto: ' + error?.message);
    }
  };

  const resetForm = () => {
    setFormData({
      nome: '',
      descrizione: '',
      categoria: '',
      prezzo: '',
      prezzoScontato: '',
      immagine: '',
      disponibile: true,
      nuovo: false,
      inEvidenza: false,
      ordine: 0
    });
    setEditingProduct(null);
    setShowForm(false);
  };

  if (loading) {
    return <div className="text-center py-8">Caricamento prodotti...</div>;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold">Prodotti ({products?.length})</h2>
        <Button onClick={() => setShowForm(!showForm)}>
          <Icon name={showForm ? 'X' : 'Plus'} size={20} />
          {showForm ? 'Annulla' : 'Aggiungi Prodotto'}
        </Button>
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="bg-muted/30 rounded-lg p-6 mb-6">
          <h3 className="text-lg font-semibold mb-4">
            {editingProduct ? 'Modifica Prodotto' : 'Nuovo Prodotto'}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Nome Prodotto *"
              value={formData?.nome}
              onChange={(e) => setFormData({ ...formData, nome: e?.target?.value })}
              required
            />
            <Input
              label="Categoria"
              value={formData?.categoria}
              onChange={(e) => setFormData({ ...formData, categoria: e?.target?.value })}
            />
            <Input
              label="Prezzo (€) *"
              type="number"
              step="0.01"
              value={formData?.prezzo}
              onChange={(e) => setFormData({ ...formData, prezzo: e?.target?.value })}
              required
            />
            <Input
              label="Prezzo Scontato (€)"
              type="number"
              step="0.01"
              value={formData?.prezzoScontato}
              onChange={(e) => setFormData({ ...formData, prezzoScontato: e?.target?.value })}
            />
            <Input
              label="URL Immagine"
              value={formData?.immagine}
              onChange={(e) => setFormData({ ...formData, immagine: e?.target?.value })}
              className="md:col-span-2"
            />
            <div className="md:col-span-2">
              <label className="block text-sm font-medium mb-2">Descrizione</label>
              <textarea
                value={formData?.descrizione}
                onChange={(e) => setFormData({ ...formData, descrizione: e?.target?.value })}
                rows={3}
                className="w-full px-4 py-2 rounded-lg border border-input bg-background"
              />
            </div>
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData?.disponibile}
                  onChange={(e) => setFormData({ ...formData, disponibile: e?.target?.checked })}
                  className="rounded"
                />
                <span className="text-sm">Disponibile</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData?.nuovo}
                  onChange={(e) => setFormData({ ...formData, nuovo: e?.target?.checked })}
                  className="rounded"
                />
                <span className="text-sm">Nuovo</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData?.inEvidenza}
                  onChange={(e) => setFormData({ ...formData, inEvidenza: e?.target?.checked })}
                  className="rounded"
                />
                <span className="text-sm">In Evidenza</span>
              </label>
            </div>
          </div>
          <div className="flex gap-3 mt-6">
            <Button type="submit">
              <Icon name="Save" size={20} />
              {editingProduct ? 'Aggiorna' : 'Salva'}
            </Button>
            <Button type="button" variant="outline" onClick={resetForm}>
              Annulla
            </Button>
          </div>
        </form>
      )}

      {products?.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          <Icon name="Package" size={48} className="mx-auto mb-4 opacity-50" />
          <p>Nessun prodotto presente</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {products?.map((product) => (
            <div key={product?.id} className="border border-border rounded-lg p-4 hover:shadow-md transition-smooth">
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <h3 className="font-semibold text-lg">{product?.nome}</h3>
                  {product?.categoria && (
                    <span className="text-xs text-muted-foreground">{product?.categoria}</span>
                  )}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleEdit(product)}
                    className="p-2 hover:bg-muted rounded-lg transition-smooth"
                  >
                    <Icon name="Edit" size={16} />
                  </button>
                  <button
                    onClick={() => handleDelete(product?.id)}
                    className="p-2 hover:bg-destructive/10 text-destructive rounded-lg transition-smooth"
                  >
                    <Icon name="Trash2" size={16} />
                  </button>
                </div>
              </div>
              {product?.descrizione && (
                <p className="text-sm text-muted-foreground mb-3 line-clamp-2">{product?.descrizione}</p>
              )}
              <div className="flex items-center justify-between">
                <div>
                  {product?.prezzoScontato ? (
                    <div className="flex items-center gap-2">
                      <span className="text-lg font-bold text-primary">€{product?.prezzoScontato}</span>
                      <span className="text-sm line-through text-muted-foreground">€{product?.prezzo}</span>
                    </div>
                  ) : (
                    <span className="text-lg font-bold">€{product?.prezzo}</span>
                  )}
                </div>
                <div className="flex gap-2">
                  {product?.nuovo && (
                    <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full">Nuovo</span>
                  )}
                  {product?.inEvidenza && (
                    <span className="px-2 py-1 bg-yellow-100 text-yellow-700 text-xs rounded-full">Evidenza</span>
                  )}
                  {!product?.disponibile && (
                    <span className="px-2 py-1 bg-red-100 text-red-700 text-xs rounded-full">Non Disponibile</span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ProductsManager;